import {
  HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaderResponse, HttpInterceptor, HttpProgressEvent, HttpRequest, HttpResponse,
  HttpSentEvent, HttpUserEvent
} from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import { BehaviorSubject } from 'rxjs/BehaviorSubject'
import { Observable } from 'rxjs/Observable'
import { of } from 'rxjs/observable/of'
import { _throw } from 'rxjs/observable/throw'
import { catchError, distinctUntilChanged, filter, finalize, map, share, switchMap, take, tap } from 'rxjs/operators'

import { environment } from '../../../environments/environment'
import { LoginTokenModel } from '../../api/identity/models/login-token-model'
import { UserV2Service } from '../../api/identity/services/user-v2.service'

import { AppState } from '../../store'
import * as App from '../../store/actions/app.actions'
import * as Auth from '../auth/store/actions/auth.actions'
import * as fromAuth from './../auth/store/'

@Injectable()
export class AppHttpInterceptor implements HttpInterceptor {

  private tokenSubject = new BehaviorSubject<{ token: string, tokenType: string }>(null)
  private isRefreshingToken = false
  private refreshTokenUrl = `${environment.api.identityUrl}/${environment.api.user.refreshToken}`
  private loginUrl = `${environment.api.identityUrl}/${environment.api.user.login}`
  private state: LoginTokenModel
  private auth$ = this.store.select(fromAuth.getAuthState).pipe(
    distinctUntilChanged(),
    filter((state: LoginTokenModel) => !!(state && state.access_token)),
    tap((state: LoginTokenModel) => this.state = state),
    share())

  constructor(private store: Store<AppState>,
              private authService: UserV2Service) {
    this.auth$.subscribe(_ => _)
  }

  intercept(req: HttpRequest<any>,
            next: HttpHandler): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent
    | HttpResponse<any> | HttpUserEvent<any>> {

    return next.handle(this.addToken(req)).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse) {
          switch ((error as HttpErrorResponse).status) {
            case 400:
              return this.handle400Error(req, error)
            case 401:
              return this.handle401Error(req, next, error)
            case 403:
              return this.handle403Error(req, error)
            default:
              return _throw(error)
          }
        } else {
          return _throw(error)
        }
      }))
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler, err: HttpErrorResponse) {
    // 401 for /login means bad credentials
    if (req.url === this.loginUrl) {
      return _throw(err.error.message)
    }

    // if 401 from refreshToken => could not refresh token, so logout
    if (req.url === this.refreshTokenUrl) {
      return this.logout()
    }

    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true

      // Reset here so that the following requests wait until the token
      // comes back from the refreshToken call.
      this.tokenSubject.next(null)

      return this.authService.RefreshToken().pipe(
        tap(res => this.store.dispatch(new Auth.RefreshTokenSuccess(res))),
        switchMap(({access_token, token_type}) => {
          if (access_token) {
            this.tokenSubject.next({token: access_token, tokenType: token_type})
            return next.handle(this.addToken(req, access_token, token_type))
          }

          // If we don't get a new token, we are in trouble so logout.
          return this.logout()
        }),
        catchError(error => {
          // If there is an exception calling 'refreshToken', bad news so logout.
          this.store.dispatch(new App.ApiCallFailure({
            title: 'Error',
            msg: 'Could not refresh token, please log in.'
          }))
          return this.logout()
        }),
        finalize(() => {
          this.isRefreshingToken = false
        }))
    } else {
      return this.tokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(({token, tokenType}) => {
          return next.handle(this.addToken(req, token, tokenType))
        }))
    }
  }

  private handle400Error(req: HttpRequest<any>, error) {
    return _throw(error && error.error && error.error.message)
  }

  private handle403Error(req: HttpRequest<any>, err: HttpErrorResponse) {
    this.store.dispatch(new App.ApiCallFailure({
      title: 'Forbidden',
      msg: err.error && err.error.message || err.message,
    }))
    return _throw(err)
  }

  private addToken(req: HttpRequest<any>, accessToken?: string, tokenType?: string): HttpRequest<any> {
    let authorization = this.state ? `${this.state.token_type} ${this.state.access_token}` : ''
    if (accessToken && tokenType) { // override
      authorization = `${tokenType} ${accessToken}`
    }
    const login = this.loginUrl === req.url
    return authorization && !login ? req.clone({setHeaders: {Authorization: authorization}}) : req
  }

  private logout() {
    this.store.dispatch(new fromAuth.Logout({}))
    return of()
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/http/http.interceptor.ts